import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Tooltip } from '@mui/material';
import DatePicker from 'react-datepicker';
import '../Styles/PointRedemptionPolicyForm.scss';
import ElementCheckPermissionWrapper from '../../../utils/ElementCheckPermissionWrapper';
import triangleDownIcon from '../../../../assets/images/triangle-down.svg';
import leftArrowIcon from '../../../../assets/images/left-arrow.svg';
import MultiSelectDropdown from './MultiSelectDropdown';
import ProductSelectDropdown from './ProductSelectDropdown';
import { fetchAgencyOptions, fetchProviderOptions, fetchCompanyOptions, createPointExchangePolicy, fetchPointExchangePolicyDetail, fetchProductOptions } from '../Controllers/api';
import { getCookie } from '../../../utils/cookies';
import { CustomSwitch } from '../../../utils/Components/CustomSwitch';

const TAB_CONFIG = [
    { key: 'general', label: 'THÔNG TIN CHUNG' },
    { key: 'conversion', label: 'QUY TẮC ĐỔI ĐIỂM' },
];

const EXCHANGE_TYPES = [
    { value: 'discount-voucher', label: 'Voucher giảm giá' },
    { value: 'product-voucher', label: 'Voucher tặng hàng' },
    { value: 'invoice-discount', label: 'Giảm giá hóa đơn trực tiếp' },
];

const EXPIRATION_UNITS = [
    { value: 1, label: 'Ngày' },
    { value: 2, label: 'Tháng' },
    { value: 3, label: 'Năm' },
];

const VALUE_UNIT_CONFIG = {
    money: 'VNĐ',
    percent: '%',
};

const EXCHANGE_TYPE_CODES = {
    'discount-voucher': 1,
    'product-voucher': 2,
    'invoice-discount': 3,
};

const VALUE_UNIT_CODES = {
    money: 1,
    percent: 2,
};

const DEFAULT_VOUCHER_STATUS = 1;

const POLICY_CODE_PATTERN = /^[A-Za-z0-9]+$/;

const DEFAULT_FORM_STATE = {
    policyCode: '',
    policyName: '',
    company: [-1], // Mặc định là "Tất cả"
    branch: [],
    description: '',
    exchangeType: EXCHANGE_TYPES[0].value,
    voucherName: '',
    voucherValue: '',
    voucherValueUnit: 'money',
    maxDiscountValue: '',
    voucherExpirationValue: '',
    voucherExpirationUnit: EXPIRATION_UNITS[0].value,
    maxRedemptions: '',
    maxPerCustomer: '',
    requiredPoints: '',
    giftItem: '',
    invoiceValue: '',
    allow100PercentDiscount: false,
    discountFromDate: '',
    discountToDate: '',
};

const FORM_ID = 'point-policy-create-form';

const PointRedemptionPolicyCreate = (props) => {
    const history = useHistory();
    const location = useLocation();
    const [activeTab, setActiveTab] = useState('general');
    const [formState, setFormState] = useState(DEFAULT_FORM_STATE);
    const [companyOptions, setCompanyOptions] = useState([]);
    const [branchOptions, setBranchOptions] = useState([]);
    const [policyCodeFocused, setPolicyCodeFocused] = useState(false);
    const [policyNameFocused, setPolicyNameFocused] = useState(false);
    const [descriptionFocused, setDescriptionFocused] = useState(false);
    const [voucherNameFocused, setVoucherNameFocused] = useState(false);
    const [voucherValueFocused, setVoucherValueFocused] = useState(false);
    const [maxDiscountFocused, setMaxDiscountFocused] = useState(false);
    const [voucherExpirationFocused, setVoucherExpirationFocused] = useState(false);
    const [requiredPointsFocused, setRequiredPointsFocused] = useState(false);
    const [maxRedemptionsFocused, setMaxRedemptionsFocused] = useState(false);
    const [maxPerCustomerFocused, setMaxPerCustomerFocused] = useState(false);
    const [invoiceValueFocused, setInvoiceValueFocused] = useState(false);
    const [productOptions, setProductOptions] = useState([]);
    const [productLoading, setProductLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitMessage, setSubmitMessage] = useState({ type: '', text: '' });
    const [formError, setFormError] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});
    const [isLoadingCopy, setIsLoadingCopy] = useState(false);
    const fromDateInputRef = useRef(null);
    const toDateInputRef = useRef(null);
    const isProductVoucher = formState.exchangeType === 'product-voucher';
    const isInvoiceDiscount = formState.exchangeType === 'invoice-discount';
    const show100PercentToggle = formState.exchangeType === 'discount-voucher' || formState.exchangeType === 'invoice-discount';

    const {
        CREATE
    } = props.actions || {};

    const openSelect = (ref) => {
        if (!ref?.current) {
            return;
        }
        const el = ref.current;
        if (typeof el.showPicker === 'function') {
            el.showPicker();
            return;
        }
        el.focus();
        el.click();
    };

    const handleNavigateBack = () => history.push('/point-exchange-policy');

    const handleInputChange = (key) => (event) => {
        const value = event?.target?.value;
        // Các trường chỉ cho phép số nguyên dương
        const numericOnlyFields = ['voucherExpirationValue', 'requiredPoints', 'maxRedemptions', 'maxPerCustomer'];
        // voucherValue chỉ áp dụng khi là percent (giảm giá theo %)
        if (key === 'voucherValue' && formState.voucherValueUnit === 'percent') {
            // Chỉ cho phép số, loại bỏ tất cả ký tự không phải số
            let numericValue = value.replace(/[^0-9]/g, '');
            if (numericValue.startsWith('0') && numericValue.length > 1) {
                numericValue = numericValue.replace(/^0+/, '') || '';
            }
            if (numericValue === '0') {
                numericValue = '';
            }
            setFormState((prev) => ({
                ...prev,
                [key]: numericValue,
            }));
        } else if (numericOnlyFields.includes(key)) {
            let numericValue = value.replace(/[^0-9]/g, '');
            if (numericValue.startsWith('0') && numericValue.length > 1) {
                numericValue = numericValue.replace(/^0+/, '') || '';
            }
            if (numericValue === '0') {
                numericValue = '';
            }
            setFormState((prev) => ({
                ...prev,
                [key]: numericValue,
            }));
        } else {
            setFormState((prev) => ({
                ...prev,
                [key]:
                    key === 'voucherExpirationUnit'
                        ? Number(value) || ''
                        : key.includes('Value') || key.includes('max') || key.includes('required')
                        ? Number(value) || ''
                        : value,
            }));
        }
        setFieldErrors((prev) => ({ ...prev, [key]: undefined }));
    };

    // Handler riêng cho các field cần format số (VNĐ)
    const handleFormattedNumberChange = (key) => (event) => {
        const value = event?.target?.value;
        const rawNumeric = value.replace(/[.,]/g, '');
        
        let cleanedNumeric = rawNumeric;
        if (cleanedNumeric.startsWith('0') && cleanedNumeric.length > 1) {
            cleanedNumeric = cleanedNumeric.replace(/^0+/, '') || '';
        }
        if (cleanedNumeric === '0') {
            cleanedNumeric = '';
        }
        
        if (cleanedNumeric === '') {
            setFormState((prev) => ({
                ...prev,
                [key]: '',
            }));
            setFieldErrors((prev) => ({ ...prev, [key]: undefined }));
            return;
        }
        
        const parsed = parseNumber(cleanedNumeric);

        const shouldPreventZero =
            parsed === 0 ||
            parsed === '0';

        if (
            shouldPreventZero &&
            (key === 'voucherValue' ||
                (key === 'maxDiscountValue' && formState.voucherValueUnit === 'percent'))
        ) {
            setFormState((prev) => ({
                ...prev,
                [key]: '',
            }));
            return;
        }

        setFormState((prev) => ({
            ...prev,
            [key]: parsed,
        }));
        setFieldErrors((prev) => ({ ...prev, [key]: undefined }));
    };

    const handleMultiSelectChange = (key) => (values) => {
        setFormState((prev) => ({
            ...prev,
            [key]: values,
        }));
        setFieldErrors((prev) => ({ ...prev, [key]: undefined }));
    };

    const branchSelectionMeta = useMemo(() => {
        if (!Array.isArray(formState.branch) || formState.branch.length === 0) {
            return { hasSelection: false, productAgencyId: null };
        }

        const normalized = formState.branch
            .map((value) => (value != null ? Number(value) : null))
            .filter((value) => value != null && !Number.isNaN(value));

        if (normalized.length === 0) {
            return { hasSelection: false, productAgencyId: null };
        }

        if (normalized.length === 1) {
            return { hasSelection: true, productAgencyId: normalized[0] };
        }

        return { hasSelection: true, productAgencyId: -1 };
    }, [formState.branch]);
    const { hasSelection: hasBranchSelection, productAgencyId } = branchSelectionMeta;

    const hasCompanySelection = useMemo(
        () => Array.isArray(formState.company) && formState.company.length > 0,
        [formState.company]
    );

    const productSelectPlaceholder = useMemo(() => {
        if (!isProductVoucher) {
            return '';
        }
        if (!hasBranchSelection) {
            return '';
        }
        if (productLoading) {
            return '';
        }
        return '';
    }, [isProductVoucher, hasBranchSelection, productLoading]);

    const handleUnitChange = (value) => {
        setFormState((prev) => ({
            ...prev,
            voucherValueUnit: value,
        }));
    };

    const normalizeSelection = (values, isSuperAdmin = true, options = undefined) => {
        if (!values || values.length === 0) {
            return undefined;
        }
        if (values.includes(-1)) {
            // Nếu không phải superadmin, thay -1 bằng danh sách ID thực tế từ options
            if (!isSuperAdmin && options && Array.isArray(options)) {
                const actualIds = options
                    .map(opt => opt.value)
                    .filter(id => id !== -1);
                return actualIds.length > 0 ? actualIds : undefined;
            }
            return [-1];
        }
        return values;
    };

    // Helper functions để format số với dấu chấm phân cách hàng nghìn
    const formatNumber = (value) => {
        if (!value && value !== 0) return '';
        const numStr = String(value).replace(/\./g, '');
        const num = Number(numStr);
        if (isNaN(num)) return '';
        return num.toLocaleString('vi-VN');
    };

    const parseNumber = (value) => {
        if (!value) return '';
        const numStr = String(value).replace(/\./g, '').replace(/,/g, '');
        const num = Number(numStr);
        return isNaN(num) ? '' : num;
    };

    const toNumber = (value) => {
        if (value === undefined || value === null || value === '') {
            return null;
        }
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    };

    // Helper functions to convert between YYYY-MM-DD string and Date object
    const stringToDate = (dateString) => {
        if (!dateString || dateString.trim() === '') {
            return null;
        }
        const date = new Date(dateString + 'T00:00:00');
        return isNaN(date.getTime()) ? null : date;
    };

    const dateToString = (date) => {
        if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
            return '';
        }
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const mapDetailToFormState = (detail) => {
        if (!detail) {
            return DEFAULT_FORM_STATE;
        }

        const exchangeTypeCode = detail.exchangeType;
        const exchangeTypeKey = Object.keys(EXCHANGE_TYPE_CODES).find(
            (key) => EXCHANGE_TYPE_CODES[key] === exchangeTypeCode
        ) || EXCHANGE_TYPES[0].value;

        // Map companyIds và agencyIds
        const companyIds = detail.companyIds && Array.isArray(detail.companyIds) 
            ? detail.companyIds.filter((id) => id != null)
            : [];
        const agencyIds = detail.agencyIds && Array.isArray(detail.agencyIds)
            ? detail.agencyIds.filter((id) => id != null)
            : [];

        const baseState = {
            ...DEFAULT_FORM_STATE,
            policyName: detail.policyName || '',
            description: detail.description || '',
            exchangeType: exchangeTypeKey,
            requiredPoints: detail.pointsRequired || 1,
            maxRedemptions: detail.maxExchangeTotal || 1,
            maxPerCustomer: detail.maxExchangePerCustomer || 1,
            company: companyIds.length > 0 ? companyIds : [],
            branch: agencyIds.length > 0 ? agencyIds : [],
        };

        if (exchangeTypeCode === EXCHANGE_TYPE_CODES['invoice-discount']) {
            baseState.invoiceValue = detail.directDiscountAmount ? Number(detail.directDiscountAmount) : '';
        } else if (detail.voucher) {
            const voucher = detail.voucher;
            baseState.voucherName = voucher.name || '';
            
            if (exchangeTypeCode === EXCHANGE_TYPE_CODES['product-voucher']) {
                baseState.giftItem = voucher.productId ? `Sản phẩm #${voucher.productId}` : '';
            } else if (exchangeTypeCode === EXCHANGE_TYPE_CODES['discount-voucher']) {
                baseState.voucherValue = voucher.valueAmount ? Number(voucher.valueAmount) : '';
                const valueType = voucher.valueType != null ? Number(voucher.valueType) : null;
                baseState.voucherValueUnit = valueType === VALUE_UNIT_CODES.percent ? 'percent' : 'money';
                baseState.maxDiscountValue = voucher.maxValueReduce ? Number(voucher.maxValueReduce) : '';
            }

            baseState.voucherExpirationValue =
                voucher.expirationValue != null ? Number(voucher.expirationValue) : '';
            baseState.voucherExpirationUnit =
                voucher.expirationUnit != null ? voucher.expirationUnit : EXPIRATION_UNITS[0].value;
        }

        // Map eventStartDate và eventEndDate về discountFromDate và discountToDate
        if (detail.eventStartDate) {
            const fromDate = new Date(detail.eventStartDate);
            baseState.discountFromDate = fromDate.toISOString().split('T')[0]; // Format YYYY-MM-DD
        } else {
            baseState.discountFromDate = detail.discountFromDate || detail.fromDate || '';
        }
        if (detail.eventEndDate) {
            const toDate = new Date(detail.eventEndDate);
            baseState.discountToDate = toDate.toISOString().split('T')[0]; // Format YYYY-MM-DD
        } else {
            baseState.discountToDate = detail.discountToDate || detail.toDate || '';
        }
        // Map allow100PercentDiscount từ Integer (1/0) sang Boolean
        baseState.allow100PercentDiscount = detail.allow100PercentDiscount === 1 || detail.allow100Percent === 1 || false;

        return baseState;
    };

    const validateGeneralTab = () => {
        const errors = {};

        if (!formState.policyCode.trim()) {
            errors.policyCode = 'Mã chính sách không được bỏ trống.';
        } else if (!POLICY_CODE_PATTERN.test(formState.policyCode.trim())) {
            errors.policyCode = 'Mã chính sách không hợp lệ.';
        }

        if (!formState.policyName.trim()) {
            errors.policyName = 'Tên chính sách không được bỏ trống.';
        }

        // Validation cho Công ty và Chi nhánh: ít nhất một trong hai phải được chọn
        const hasCompany = formState.company.length > 0;
        const hasBranch = formState.branch.length > 0;

        if (!hasCompany && !hasBranch) {
            errors.company = 'Vui lòng chọn ít nhất 1 giá trị.';
            errors.branch = 'Vui lòng chọn ít nhất 1 giá trị.';
        }

        return errors;
    };

    const validateConversionTab = () => {
        const errors = {};

        if (!formState.requiredPoints || Number(formState.requiredPoints) <= 0) {
            errors.requiredPoints = 'Số bing cần đổi phải > 0.';
        }

        if (isInvoiceDiscount) {
            const raw = String(formState.invoiceValue ?? '').trim();
            const value = Number(raw);
            if (!value || Number.isNaN(value) || value <= 0) {
                errors.invoiceValue = 'Giá trị phải lớn hơn 0.';
            } else {
                const decimalPart = raw.split('.')[1];
                if (decimalPart && decimalPart.length > 2) {
                    errors.invoiceValue = 'Giá trị phải lớn hơn 0.';
                }
            }
        }

        if (!isInvoiceDiscount && !formState.voucherName.trim()) {
            errors.voucherName = 'Tên voucher không được bỏ trống';
        }

        if (formState.exchangeType === 'discount-voucher') {
            const value = Number(formState.voucherValue);
            if (!value || Number.isNaN(value)) {
                errors.voucherValue = 'Giá trị voucher phải > 0.';
            } else if (formState.voucherValueUnit === 'money') {
                if (value <= 0) {
                    errors.voucherValue = 'Giá trị voucher phải > 0.';
                }
            } else if (formState.voucherValueUnit === 'percent') {
                if (value < 1 || value > 100) {
                    errors.voucherValue = 'Giá trị voucher % phải nằm trong khoảng 1 - 100%.';
                }
                const maxDiscount = Number(formState.maxDiscountValue);
                if (!maxDiscount || Number.isNaN(maxDiscount) || maxDiscount <= 0) {
                    errors.maxDiscountValue = 'Giá trị phải > 0.';
                }
            }
        }

        if (formState.exchangeType === 'product-voucher' && !formState.giftItem) {
            errors.giftItem = 'Hàng tặng không được bỏ trống';
        }

        if (!isInvoiceDiscount) {
            const expiry = Number(formState.voucherExpirationValue);
            if (!expiry || Number.isNaN(expiry) || expiry <= 0) {
                errors.voucherExpirationValue = 'Hạn sử dụng voucher phải > 0.';
            }

            if (!formState.maxRedemptions || Number(formState.maxRedemptions) <= 0) {
                errors.maxRedemptions = 'Số lượng đổi tối đa phải > 0.';
            }

            if (!formState.maxPerCustomer || Number(formState.maxPerCustomer) <= 0) {
                errors.maxPerCustomer = 'Số lượng đổi tối đa/khách hàng phải > 0.';
            }

            const maxRedemptions = Number(formState.maxRedemptions);
            const maxPerCustomer = Number(formState.maxPerCustomer);
            if (!Number.isNaN(maxRedemptions) && !Number.isNaN(maxPerCustomer) && maxPerCustomer > maxRedemptions) {
                errors.maxPerCustomer = 'Số lượng đổi tối đa/khách hàng phải ≤ Số lượng đổi tối đa';
            }
        }

        // Validation cho trường Từ ngày và Đến ngày trong Thời gian áp dụng
        if (formState.allow100PercentDiscount) {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const todayStr = today.toISOString().split('T')[0];

            // Validation cho Từ ngày
            if (!formState.discountFromDate || formState.discountFromDate.trim() === '') {
                errors.discountFromDate = 'Từ ngày không được bỏ trống';
            } else {
                const fromDateStr = formState.discountFromDate;

                // Kiểm tra không được lớn hơn Đến ngày
                if (formState.discountToDate && formState.discountToDate.trim() !== '') {
                    if (fromDateStr > formState.discountToDate) {
                        errors.discountFromDate = 'Từ ngày phải nhỏ hơn hoặc bằng Đến ngày';
                    }
                }
            }

            // Validation cho Đến ngày
            if (!formState.discountToDate || formState.discountToDate.trim() === '') {
                errors.discountToDate = 'Đến ngày không được bỏ trống';
            } else {
                const toDateStr = formState.discountToDate;

                // Kiểm tra phải lớn hơn hoặc bằng Từ ngày
                if (formState.discountFromDate && formState.discountFromDate.trim() !== '') {
                    if (toDateStr < formState.discountFromDate) {
                        errors.discountToDate = 'Đến ngày phải lớn hơn hoặc bằng Từ ngày';
                    }
                }
            }
        }

        return errors;
    };

    const validateForm = () => {
        return {
            ...validateGeneralTab(),
            ...validateConversionTab(),
        };
    };

    const buildPayload = (policyStatus) => {
        // Lấy thông tin user từ cookie để kiểm tra super admin
        const userCookie = getCookie();
        const userData = userCookie ? JSON.parse(userCookie) : null;
        const isSuperAdmin = userData !== null && userData.isSuperAdmin === 1;

        const exchangeTypeCode = EXCHANGE_TYPE_CODES[formState.exchangeType];
        const basePayload = {
            policyCode: formState.policyCode.trim(),
            policyName: formState.policyName.trim(),
            description: formState.description.trim() || null,
            exchangeType: exchangeTypeCode,
            policyStatus,
            pointsRequired: Number(formState.requiredPoints) || 0,
            maxExchangeTotal: !isInvoiceDiscount ? toNumber(formState.maxRedemptions) : null,
            maxExchangePerCustomer: !isInvoiceDiscount ? toNumber(formState.maxPerCustomer) : null,
            directDiscountAmount: null,
            voucherExchange: null,
        };

        // Chỉ thêm companyIds và agencyIds nếu có giá trị
        const normalizedCompanyIds = normalizeSelection(formState.company, isSuperAdmin, companyOptions);
        const normalizedAgencyIds = normalizeSelection(formState.branch, isSuperAdmin, branchOptions);
        
        if (normalizedCompanyIds !== undefined) {
            basePayload.companyIds = normalizedCompanyIds;
        }
        if (normalizedAgencyIds !== undefined) {
            basePayload.agencyIds = normalizedAgencyIds;
        }

        // Map allow100PercentDiscount (1 = bật, 0 = tắt)
        basePayload.allow100PercentDiscount = formState.allow100PercentDiscount ? 1 : 0;

        // Map discountFromDate và discountToDate thành eventStartDate và eventEndDate
        if (formState.allow100PercentDiscount) {
        if (formState.discountFromDate) {
            // Convert date string (YYYY-MM-DD) to Timestamp format
            const fromDate = new Date(formState.discountFromDate);
            fromDate.setHours(0, 0, 0, 0);
            basePayload.eventStartDate = fromDate.toISOString();
        }
        if (formState.discountToDate) {
            // Convert date string (YYYY-MM-DD) to Timestamp format
            const toDate = new Date(formState.discountToDate);
            toDate.setHours(23, 59, 59, 999);
            basePayload.eventEndDate = toDate.toISOString();
        }
        } else {

            basePayload.eventStartDate = null;
            basePayload.eventEndDate = null;
        }

        if (exchangeTypeCode === EXCHANGE_TYPE_CODES['invoice-discount']) {
            basePayload.directDiscountAmount = toNumber(formState.invoiceValue) || 0;
        } else {
            const voucher = {
                voucherName: formState.voucherName.trim(),
                valueType: null,
                valueAmount: null,
                maxValueReduce: null,
                productId: null,
                expirationUnit: formState.voucherExpirationUnit,
                expirationValue: toNumber(formState.voucherExpirationValue),
                voucherStatus: DEFAULT_VOUCHER_STATUS,
            };

            if (exchangeTypeCode === EXCHANGE_TYPE_CODES['discount-voucher']) {
                voucher.valueType = VALUE_UNIT_CODES[formState.voucherValueUnit] || VALUE_UNIT_CODES.money;
                voucher.valueAmount = toNumber(formState.voucherValue) || 0;
                voucher.maxValueReduce =
                    formState.voucherValueUnit === 'percent'
                        ? toNumber(formState.maxDiscountValue) || 0
                        : null;
            }

            if (exchangeTypeCode === EXCHANGE_TYPE_CODES['product-voucher']) {
                voucher.valueType = VALUE_UNIT_CODES.money;
                voucher.valueAmount = null;
                voucher.maxValueReduce = null;
                voucher.productId =
                    formState.giftItem !== '' && formState.giftItem != null
                        ? Number(formState.giftItem)
                        : null;
            }

            basePayload.voucherExchange = voucher;
        }
        return basePayload;
    };

    const handleSubmitPolicy = async (policyStatus) => {
        setFormError('');
        setSubmitMessage({ type: '', text: '' });
        const generalErrors = validateGeneralTab();
        const conversionErrors = validateConversionTab();
        const errors = { ...generalErrors, ...conversionErrors };
        setFieldErrors(errors);
        
        if (Object.keys(errors).length > 0) {
            // Kiểm tra lỗi ở tab Thông tin chung
            const generalErrorKeys = ['policyCode', 'policyName', 'company', 'branch'];
            const hasGeneralError = generalErrorKeys.some(key => errors[key]);
            
            // Kiểm tra lỗi ở tab Quy tắc đổi điểm
            const conversionErrorKeys = ['requiredPoints', 'invoiceValue', 'voucherName', 'voucherValue', 'maxDiscountValue', 'giftItem', 'voucherExpirationValue', 'maxRedemptions', 'maxPerCustomer', 'discountFromDate', 'discountToDate'];
            const hasConversionError = conversionErrorKeys.some(key => errors[key]);
            
            if (hasGeneralError) {
                toast.error('Bạn chưa nhập đầy đủ thông tin tại tab [Thông tin chung]');
            }
            if (hasConversionError) {
                toast.error('Bạn chưa nhập đầy đủ thông tin tại tab [Quy tắc đổi điểm]');
            }
            return;
        }
        const payload = buildPayload(policyStatus);
        setIsSubmitting(true);
        try {
            const response = await createPointExchangePolicy(payload);
            if (response?.code === 200) {
                toast.success('Thành công');
                history.push('/point-exchange-policy');
                return;
            } else {
                const errorMessage = response?.message || response?.data?.message || 'Không thể tạo chính sách. Vui lòng thử lại.';
                const errorCode = response?.code || response?.data?.code;
                
                if (errorMessage.includes('Mã chính sách đã tồn tại') || 
                    errorCode === 'E110' || 
                    errorCode === 110) {
                    setFieldErrors({ policyCode: errorMessage });
                }
            }
        } catch (error) {
            const errorMessage = error?.response?.data?.message || error?.message || 'Không thể tạo chính sách. Vui lòng thử lại.';
            const errorCode = error?.response?.data?.code || error?.code;
            
            if (errorMessage.includes('Mã chính sách đã tồn tại') || 
                errorCode === 'E110' || 
                errorCode === 110) {
                setFieldErrors({ policyCode: errorMessage });
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    // Load chi nhánh trước (với quyền user), sau đó từ đó lấy danh sách công ty tương ứng
    useEffect(() => {
        let isMounted = true;
        const loadAgenciesAndCompanies = async () => {
            try {
                // Lấy thông tin user từ cookie để kiểm tra super admin
                const userCookie = getCookie();
                const userData = userCookie ? JSON.parse(userCookie) : null;
                const isSuperAdmin = userData !== null && userData.isSuperAdmin === 1;
                
                const companyResponse = await fetchCompanyOptions();
                
                if (!isMounted) {
                    return;
                }

                if (companyResponse?.status === 200 && Array.isArray(companyResponse.data?.data)) {
                    const companyList = companyResponse.data.data;
                    let filteredCompanies = companyList;
                    
                    if (!isSuperAdmin) {
                const agencyResponse = await fetchAgencyOptions({
                    companyId: [-1],
                    type: 3,
                });
                        
                        if (!isMounted) {
                            return;
                        }

                if (agencyResponse?.status === 200 && Array.isArray(agencyResponse.data?.agencyInfoResList)) {
                    const companyNamesSet = new Set();
                    agencyResponse.data.agencyInfoResList.forEach((agency) => {
                        if (agency.companyName) {
                            companyNamesSet.add(agency.companyName);
                        }
                    });

                            filteredCompanies = companyList.filter((company) =>
                                companyNamesSet.has(company.name)
                            );
                        }
                    }

                    if (!isMounted) {
                        return;
                    }

                        const options = filteredCompanies.map((item) => ({
                            label: item.name,
                            value: item.id,
                        }));
                        setCompanyOptions([{ label: 'Tất cả', value: -1 }, ...options]);
                    } else {
                    if (isMounted) {
                        setCompanyOptions([]);
                    }
                }
            } catch (error) {
                console.error('Failed to load agencies and companies', error);
                if (isMounted) {
                    setCompanyOptions([]);
                }
            }
        };

        loadAgenciesAndCompanies();
        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const copyFromId = searchParams.get('copyFrom');
        
        if (!copyFromId) {
            return;
        }

        let isMounted = true;
        const loadPolicyForCopy = async () => {
            setIsLoadingCopy(true);
            try {
                const response = await fetchPointExchangePolicyDetail(copyFromId);
                if (!isMounted) {
                    return;
                }
                if (response?.code === 200 && response.data) {
                    const mappedState = mapDetailToFormState(response.data);
                    setFormState(mappedState);
                } else {
                    setFormError('Không thể tải thông tin chính sách để sao chép.');
                }
            } catch (error) {
                console.error('Failed to load policy for copy', error);
                if (isMounted) {
                    setFormError('Không thể tải thông tin chính sách để sao chép.');
                }
            } finally {
                if (isMounted) {
                    setIsLoadingCopy(false);
                }
            }
        };

        loadPolicyForCopy();
        return () => {
            isMounted = false;
        };
    }, [location.search]);

    useEffect(() => {
        let isMounted = true;
        const loadAgencies = async () => {
            try {
                // Lấy thông tin user từ cookie để kiểm tra super admin
                const userCookie = getCookie();
                const userData = userCookie ? JSON.parse(userCookie) : null;
                const isSuperAdmin = userData !== null && userData.isSuperAdmin === 1;
                
                const selectedCompanyIds =
                    formState.company.length === 0 || formState.company.includes(-1)
                        ? [-1]
                        : formState.company;
                
                let allBranches = [];
                
                if (isSuperAdmin) {
                    const [agencyResponse, providerResponse] = await Promise.all([
                        fetchAgencyOptions({
                            companyId: selectedCompanyIds,
                        }),
                        fetchProviderOptions({
                            companyId: selectedCompanyIds,
                        })
                    ]);

                    if (!isMounted) {
                        return;
                    }

                    if (agencyResponse?.status === 200 && Array.isArray(agencyResponse.data?.agencyInfoResList)) {
                        allBranches = [...allBranches, ...agencyResponse.data.agencyInfoResList];
                    }

                    if (providerResponse?.status === 200 && Array.isArray(providerResponse.data?.agencyInfoResList)) {
                        allBranches = [...allBranches, ...providerResponse.data.agencyInfoResList];
                    }
                } else {
                const response = await fetchAgencyOptions({
                    companyId: selectedCompanyIds,
                    type: 3,
                });
                if (!isMounted) {
                    return;
                }
                if (
                    response?.status === 200 &&
                    Array.isArray(response.data?.agencyInfoResList)
                ) {
                        allBranches = [...allBranches, ...response.data.agencyInfoResList];
                    }
                }

                if (!isMounted) {
                    return;
                }

                const uniqueBranches = Array.from(
                    new Map(allBranches.map(item => [item.id, item])).values()
                );
                const options = uniqueBranches.map((item) => ({
                        label: item.name,
                        value: item.id,
                    }));
                    setBranchOptions([{ label: 'Tất cả', value: -1 }, ...options]);

                    setFormState((prev) => {
                        if (prev.branch.includes(-1)) {
                            const newSelection = [-1, ...options.map((opt) => opt.value)];
                            const sameLength = prev.branch.length === newSelection.length;
                            const sameValues =
                                sameLength &&
                                prev.branch.every((value, idx) => newSelection[idx] === value);
                            return sameValues ? prev : { ...prev, branch: newSelection };
                        }

                        const filteredBranch = prev.branch.filter((value) =>
                            options.some((opt) => opt.value === value)
                        );
                        const sameSelection =
                            filteredBranch.length === prev.branch.length &&
                            filteredBranch.every((value, idx) => value === prev.branch[idx]);
                        return sameSelection ? prev : { ...prev, branch: filteredBranch };
                    });
            } catch (error) {
                console.error('Failed to fetch agency list', error);
                if (isMounted) {
                    setBranchOptions([{ label: 'Tất cả', value: -1 }]);
                    setFormState((prev) =>
                        prev.branch.length > 0 ? { ...prev, branch: [] } : prev
                    );
                }
            }
        };

        loadAgencies();

        return () => {
            isMounted = false;
        };
    }, [formState.company]);

    useEffect(() => {
        let isMounted = true;

        if (!isProductVoucher) {
            setProductOptions([]);
            setProductLoading(false);
            setFormState((prev) => (prev.giftItem ? { ...prev, giftItem: '' } : prev));
            return () => {
                isMounted = false;
            };
        }

        const hasCompanySelection =
            Array.isArray(formState.company) && formState.company.length > 0;

        if (!hasBranchSelection && !hasCompanySelection) {
            setProductOptions([]);
            setProductLoading(false);
            setFormState((prev) => (prev.giftItem ? { ...prev, giftItem: '' } : prev));
            return () => {
                isMounted = false;
            };
        }

        const companyIds = hasCompanySelection ? normalizeSelection(formState.company) : undefined;
        const effectiveAgencyId = hasBranchSelection ? productAgencyId : null;

        setProductLoading(true);
        fetchProductOptions({
            companyId: companyIds,
            agencyId: effectiveAgencyId,
            status: [-1],
            type: [-1],
            isPackage: [-1],
            idCategory: [-1],
            page: 1,
            size: 200,
        })
            .then((response) => {
                if (!isMounted) {
                    return;
                }
                if (response?.status === 200) {
                    const products =
                        response?.data?.products ||
                        response?.data?.data?.products ||
                        response?.data?.productList ||
                        [];
                    if (Array.isArray(products)) {
                        // Chỉ lấy sản phẩm đang hoạt động (status = 2) 
                        const activeProducts = products.filter(
                            (product) => Number(product?.status) === 2
                        );
                        const mapped = activeProducts.map((product) => ({
                            label: product.name || product.productName || '',
                            value: product.id ?? product.productId,
                        }));
                        setProductOptions(mapped);
                        setFormState((prev) => {
                            if (!prev.giftItem) {
                                return prev;
                            }
                            const exists = mapped.some((item) => item.value === prev.giftItem);
                            return exists ? prev : { ...prev, giftItem: '' };
                        });
                        return;
                    }
                }
                setProductOptions([]);
                setFormState((prev) => (prev.giftItem ? { ...prev, giftItem: '' } : prev));
            })
            .catch(() => {
                if (!isMounted) {
                    return;
                }
                setProductOptions([]);
                setFormState((prev) => (prev.giftItem ? { ...prev, giftItem: '' } : prev));
            })
            .finally(() => {
                if (isMounted) {
                    setProductLoading(false);
                }
            });

        return () => {
            isMounted = false;
        };
    }, [isProductVoucher, hasBranchSelection, productAgencyId, formState.company]);

    return (
        <div className={`point-policy-form point-policy-form--${activeTab}`}>
            <button type="button" className="back-trigger" onClick={handleNavigateBack}>
                <img src={leftArrowIcon} alt="" />
                <span>Thêm mới chính sách đổi điểm</span>
            </button>

            <div className="form-surface">
                <div className="form-tabs">
                    {TAB_CONFIG.map((tab) => (
                        <button
                            key={tab.key}
                            type="button"
                            className={`tab-trigger ${activeTab === tab.key ? 'is-active' : ''}`}
                            onClick={() => setActiveTab(tab.key)}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {isLoadingCopy && (
                    <div className="form-alert form-alert--info">Đang tải thông tin chính sách để sao chép...</div>
                )}
                <form
                    id={FORM_ID}
                    className="tab-body"
                    noValidate
                    onSubmit={(event) => {
                        event.preventDefault();
                        handleSubmitPolicy(1);
                    }}
                >
                    {activeTab === 'general' && (
                        <div className="general-form">
                            <div className="general-grid">
                                <div
                                    className={`text-field text-field--line text-field--floating ${
                                        policyCodeFocused || formState.policyCode ? 'is-floating' : ''
                                    }`}
                                >
                                    <label>Mã chính sách (*)</label>
                                    <input
                                        type="text"
                                        maxLength={50}
                                        value={formState.policyCode}
                                        onChange={handleInputChange('policyCode')}
                                        onFocus={() => setPolicyCodeFocused(true)}
                                        onBlur={() => setPolicyCodeFocused(false)}
                                    />
                                    {fieldErrors.policyCode && (
                                        <div className="field-error">{fieldErrors.policyCode}</div>
                                    )}
                                </div>

                                <div
                                    className={`text-field text-field--line text-field--floating ${
                                        policyNameFocused || formState.policyName ? 'is-floating' : ''
                                    }`}
                                >
                                    <label>Tên chính sách (*)</label>
                                    <input
                                        type="text"
                                        maxLength={255}
                                        value={formState.policyName}
                                        onChange={handleInputChange('policyName')}
                                        onFocus={() => setPolicyNameFocused(true)}
                                        onBlur={() => setPolicyNameFocused(false)}
                                    />
                                    {fieldErrors.policyName && (
                                        <div className="field-error">{fieldErrors.policyName}</div>
                                    )}
                                </div>
                            </div>

                            <div className="general-grid">
                                <div className="multi-select-wrapper">
                                    <MultiSelectDropdown
                                        label="Công ty áp dụng"
                                        options={companyOptions}
                                        selectedValues={formState.company}
                                        onSelectionChange={handleMultiSelectChange('company')}
                                        placeholder=""
                                    />
                                    {fieldErrors.company && (
                                        <div className="field-error">{fieldErrors.company}</div>
                                    )}
                                </div>

                                <div className="multi-select-wrapper">
                                    <MultiSelectDropdown
                                        label="Chi nhánh áp dụng"
                                        options={branchOptions}
                                        selectedValues={formState.branch}
                                        onSelectionChange={handleMultiSelectChange('branch')}
                                        placeholder=""
                                    />
                                    {fieldErrors.branch && (
                                        <div className="field-error">{fieldErrors.branch}</div>
                                    )}
                                </div>
                            </div>

                            <div
                                className={`text-field text-field--line text-field--floating is-wide ${
                                    descriptionFocused || formState.description ? 'is-floating' : ''
                                }`}
                            >
                                <label>Mô tả</label>
                                <input
                                    type="text"
                                    maxLength={500}
                                    value={formState.description}
                                    onChange={handleInputChange('description')}
                                    onFocus={() => setDescriptionFocused(true)}
                                    onBlur={() => setDescriptionFocused(false)}
                                />
                            </div>
                        </div>
                    )}

                    {activeTab === 'conversion' && (
                        <>
                            <div className="text-field text-field--line text-field--floating has-full-select is-standalone is-floating">
                                <label>Loại quy đổi (*)</label>
                                <div className="select-wrapper">
                                    <select value={formState.exchangeType} onChange={handleInputChange('exchangeType')}>
                                        {EXCHANGE_TYPES.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                    <img src={triangleDownIcon} alt="" className="chevron-icon" />
                                </div>
                            </div>

                            <div className="section-heading">Thông tin quy đổi</div>

                            {!isInvoiceDiscount && (
                                <div
                                    className={`voucher-row voucher-row--${formState.voucherValueUnit} ${
                                        isProductVoucher ? 'voucher-row--product' : ''
                                    }`}
                                >
                                    <div
                                        className={`text-field text-field--line text-field--floating ${
                                            voucherNameFocused || formState.voucherName ? 'is-floating' : ''
                                        }`}
                                    >
                                        <label>Tên voucher (*)</label>
                                        <input
                                            type="text"
                                            maxLength={255}
                                            value={formState.voucherName}
                                            onChange={handleInputChange('voucherName')}
                                            onFocus={() => setVoucherNameFocused(true)}
                                            onBlur={() => setVoucherNameFocused(false)}
                                        />
                                        {fieldErrors.voucherName && (
                                            <div className="field-error">{fieldErrors.voucherName}</div>
                                        )}
                                    </div>

                                    {isProductVoucher ? (
                                        <div className="product-voucher-select">
                                            <ProductSelectDropdown
                                                label="Hàng tặng (*)"
                                                options={productOptions}
                                                selectedValue={formState.giftItem || null}
                                                onChange={(value) => {
                                                    setFormState((prev) => ({ ...prev, giftItem: value || '' }));
                                                    setFieldErrors((prev) => ({ ...prev, giftItem: undefined }));
                                                }}
                                                placeholder={productSelectPlaceholder}
                                                disabled={(!hasBranchSelection && !hasCompanySelection) || productLoading}
                                            />
                                            {fieldErrors.giftItem && (
                                                <div className="field-error">{fieldErrors.giftItem}</div>
                                            )}
                                        </div>
                                    ) : (
                                        <div
                                            className={`text-field text-field--line text-field--floating has-value-toggle ${
                                                voucherValueFocused ||
                                                (formState.voucherValue !== '' &&
                                                    formState.voucherValue !== null &&
                                                    formState.voucherValue !== undefined)
                                                    ? 'is-floating'
                                                    : ''
                                            }`}
                                        >
                                            <label>Giá trị voucher (*)</label>
                                            <div className="value-row">
                                                <input
                                                    type="text"
                                                    inputMode={formState.voucherValueUnit === 'money' ? 'numeric' : 'numeric'}
                                                    pattern={formState.voucherValueUnit === 'percent' ? '[0-9]*' : undefined}
                                                    value={formState.voucherValueUnit === 'money' 
                                                        ? formatNumber(formState.voucherValue) 
                                                        : formState.voucherValue}
                                                    onChange={formState.voucherValueUnit === 'money'
                                                        ? handleFormattedNumberChange('voucherValue')
                                                        : handleInputChange('voucherValue')}
                                                    onFocus={() => setVoucherValueFocused(true)}
                                                    onBlur={() => setVoucherValueFocused(false)}
                                                    onKeyPress={(e) => {
                                                        if (formState.voucherValueUnit === 'percent') {
                                                            if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab' && e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') {
                                                                e.preventDefault();
                                                            }
                                                        }
                                                    }}
                                                />
                                                {!isProductVoucher && (
                                                    <div className="value-toggle" role="group" aria-label="Đơn vị voucher">
                                                        {Object.entries(VALUE_UNIT_CONFIG).map(([key, label]) => (
                                                            <button
                                                                key={key}
                                                                type="button"
                                                                className={formState.voucherValueUnit === key ? 'is-active' : ''}
                                                                onClick={() => handleUnitChange(key)}
                                                            >
                                                                {label}
                                                            </button>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        {fieldErrors.voucherValue && !isProductVoucher && (
                                            <div className="field-error">{fieldErrors.voucherValue}</div>
                                        )}
                                        </div>
                                    )}

                                    {formState.voucherValueUnit === 'percent' && !isProductVoucher && (
                                        <div
                                            className={`text-field text-field--line text-field--floating ${
                                                maxDiscountFocused || formState.maxDiscountValue ? 'is-floating' : ''
                                            }`}
                                        >
                                            <label>Giá trị VNĐ giảm tối đa (*)</label>
                                            <input
                                                type="text"
                                                min="0"
                                                value={formatNumber(formState.maxDiscountValue)}
                                                onChange={handleFormattedNumberChange('maxDiscountValue')}
                                                onFocus={() => setMaxDiscountFocused(true)}
                                                onBlur={() => setMaxDiscountFocused(false)}
                                            />
                                            {fieldErrors.maxDiscountValue && (
                                                <div className="field-error">{fieldErrors.maxDiscountValue}</div>
                                            )}
                                        </div>
                                    )}

                                    <div
                                        className={`text-field text-field--line text-field--floating ${
                                            voucherExpirationFocused || formState.voucherExpirationValue ? 'is-floating' : ''
                                        }`}
                                    >
                                        <label>Hạn sử dụng voucher (*)</label>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            pattern="[0-9]*"
                                            value={formState.voucherExpirationValue}
                                            onChange={handleInputChange('voucherExpirationValue')}
                                            onFocus={() => setVoucherExpirationFocused(true)}
                                            onBlur={() => setVoucherExpirationFocused(false)}
                                            onKeyPress={(e) => {
                                                if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab' && e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') {
                                                    e.preventDefault();
                                                }
                                            }}
                                        />
                                            {fieldErrors.voucherExpirationValue && (
                                                <div className="field-error">{fieldErrors.voucherExpirationValue}</div>
                                            )}
                                    </div>

                                    <div className="text-field text-field--line text-field--floating is-floating voucher-expiration-context">
                                        <div className="inline-select">
                                            <div className="select-wrapper compact">
                                                <select
                                                    value={formState.voucherExpirationUnit}
                                                    onChange={handleInputChange('voucherExpirationUnit')}
                                                >
                                                    {EXPIRATION_UNITS.map((option) => (
                                                        <option key={option.value} value={option.value}>
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </select>
                                                <img src={triangleDownIcon} alt="" className="chevron-icon" />
                                            </div>
                                            <span className="inline-unit">(Tính từ ngày phát sinh)</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div
                                className={`form-grid conversion-limits-grid ${
                                    isInvoiceDiscount ? 'conversion-limits-grid--invoice' : ''
                                }`}
                            >
                                {isInvoiceDiscount ? (
                                    <div className="conversion-invoice-row">
                                        <div
                                            className={`text-field text-field--line text-field--floating has-suffix ${
                                                requiredPointsFocused || formState.requiredPoints ? 'is-floating' : ''
                                            }`}
                                        >
                                            <label>Số bing (*)</label>
                                            <div className="input-with-suffix">
                                                <input
                                                    type="text"
                                                    inputMode="numeric"
                                                    pattern="[0-9]*"
                                                    value={formState.requiredPoints}
                                                    onChange={handleInputChange('requiredPoints')}
                                                    onFocus={() => setRequiredPointsFocused(true)}
                                                    onBlur={() => setRequiredPointsFocused(false)}
                                                    onKeyPress={(e) => {
                                                        if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab' && e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') {
                                                            e.preventDefault();
                                                        }
                                                    }}
                                                />
                                                <span className="suffix-label">bing</span>
                                            </div>
                                            {fieldErrors.requiredPoints && (
                                                <div className="field-error">{fieldErrors.requiredPoints}</div>
                                            )}
                                        </div>
                                        <span className="conversion-equals">=</span>
                                        <div
                                            className={`text-field text-field--line text-field--floating has-suffix ${
                                                invoiceValueFocused || formState.invoiceValue ? 'is-floating' : ''
                                            }`}
                                        >
                                            <label>Giá trị (*)</label>
                                            <div className="input-with-suffix">
                                                <input
                                                    type="text"
                                                    min="0"
                                                    value={formatNumber(formState.invoiceValue)}
                                                    onChange={handleFormattedNumberChange('invoiceValue')}
                                                    onFocus={() => setInvoiceValueFocused(true)}
                                                    onBlur={() => setInvoiceValueFocused(false)}
                                                />
                                                <span className="suffix-label">VNĐ</span>
                                            </div>
                                            {fieldErrors.invoiceValue && (
                                                <div className="field-error">{fieldErrors.invoiceValue}</div>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div
                                            className={`text-field text-field--line text-field--floating has-suffix form-field--second-row ${
                                                requiredPointsFocused || formState.requiredPoints ? 'is-floating' : ''
                                            }`}
                                        >
                                            <label>Số bing cần đổi (*)</label>
                                            <div className="input-with-suffix">
                                                <input
                                                    type="text"
                                                    inputMode="numeric"
                                                    pattern="[0-9]*"
                                                    value={formState.requiredPoints}
                                                    onChange={handleInputChange('requiredPoints')}
                                                    onFocus={() => setRequiredPointsFocused(true)}
                                                    onBlur={() => setRequiredPointsFocused(false)}
                                                    onKeyPress={(e) => {
                                                        if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab' && e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') {
                                                            e.preventDefault();
                                                        }
                                                    }}
                                                />
                                                <span className="suffix-label">bing</span>
                                            </div>
                                            {fieldErrors.requiredPoints && (
                                                <div className="field-error">{fieldErrors.requiredPoints}</div>
                                            )}
                                        </div>

                                        <div
                                            className={`text-field text-field--line text-field--floating form-field--second-row max-redemptions-field ${
                                                maxRedemptionsFocused || formState.maxRedemptions ? 'is-floating' : ''
                                            }`}
                                        >
                                            <label>Số lượng đổi tối đa</label>
                                            <input
                                                type="text"
                                                inputMode="numeric"
                                                pattern="[0-9]*"
                                                value={formState.maxRedemptions}
                                                onChange={handleInputChange('maxRedemptions')}
                                                onFocus={() => setMaxRedemptionsFocused(true)}
                                                onBlur={() => setMaxRedemptionsFocused(false)}
                                                onKeyPress={(e) => {
                                                    if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab' && e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') {
                                                        e.preventDefault();
                                                    }
                                                }}
                                            />
                                            {fieldErrors.maxRedemptions && (
                                                <div className="field-error">{fieldErrors.maxRedemptions}</div>
                                            )}
                                        </div>

                                        <div
                                            className={`text-field text-field--line text-field--floating form-field--second-row ${
                                                maxPerCustomerFocused || formState.maxPerCustomer ? 'is-floating' : ''
                                            }`}
                                        >
                                            <label>Số lượng đổi tối đa/khách hàng</label>
                                            <input
                                                type="text"
                                                inputMode="numeric"
                                                pattern="[0-9]*"
                                                value={formState.maxPerCustomer}
                                                onChange={handleInputChange('maxPerCustomer')}
                                                onFocus={() => setMaxPerCustomerFocused(true)}
                                                onBlur={() => setMaxPerCustomerFocused(false)}
                                                onKeyPress={(e) => {
                                                    if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab' && e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') {
                                                        e.preventDefault();
                                                    }
                                                }}
                                            />
                                            {fieldErrors.maxPerCustomer && (
                                                <div className="field-error">{fieldErrors.maxPerCustomer}</div>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>

                            {show100PercentToggle && (
                                <div className="discount-100-percent-section">
                                    <div className="discount-100-percent-toggle">
                                        <CustomSwitch
                                            checked={formState.allow100PercentDiscount || false}
                                            onChange={(e) => {
                                                const checked = e?.target?.checked ?? false;
                                                setFormState((prev) => ({
                                                    ...prev,
                                                    allow100PercentDiscount: checked,
                                                    discountFromDate: checked ? prev.discountFromDate : '',
                                                    discountToDate: checked ? prev.discountToDate : '',
                                                }));
                                                setFieldErrors((prev) => ({
                                                    ...prev,
                                                    discountFromDate: undefined,
                                                    discountToDate: undefined,
                                                }));
                                            }}
                                        />
                                        <label className="toggle-label">
                                            Cho phép giảm đến 100% giá trị đơn hàng
                                        </label>
                                        <Tooltip 
                                            title="Giá trị giảm thực tế chịu ảnh hưởng trong phạm vi thanh toán tối thiểu 5.000 VNĐ"
                                            arrow
                                            placement="top"
                                            slotProps={{
                                                tooltip: {
                                                    sx: {
                                                        fontSize: '14px',
                                                        padding: '12px 16px',
                                                        maxWidth: '400px',
                                                        '& .MuiTooltip-arrow': {
                                                            color: 'rgba(0, 0, 0, 0.87)',
                                                        }
                                                    }
                                                }
                                            }}
                                        >
                                            <span className="info-icon">?</span>
                                        </Tooltip>
                                    </div>

                                    {formState.allow100PercentDiscount && (
                                        <div className="discount-date-range">
                                            <div
                                                className={`text-field text-field--line text-field--floating ${
                                                    formState.discountFromDate ? 'is-floating' : ''
                                                }`}
                                            >
                                                <label>Từ ngày</label>
                                                <div className="date-picker-wrapper">
                                                    <DatePicker
                                                        selected={stringToDate(formState.discountFromDate)}
                                                        onChange={(date) => {
                                                            const dateString = dateToString(date);
                                                            setFormState((prev) => ({
                                                                ...prev,
                                                                discountFromDate: dateString,
                                                            }));
                                                            setFieldErrors((prev) => ({
                                                                ...prev,
                                                                discountFromDate: undefined,
                                                            }));
                                                            
                                                            if (formState.discountToDate && formState.discountToDate.trim() !== '') {
                                                                if (dateString && dateString > formState.discountToDate) {
                                                                    setFieldErrors((prev) => ({
                                                                        ...prev,
                                                                        discountToDate: 'Đến ngày phải lớn hơn hoặc bằng Từ ngày',
                                                                    }));
                                                                } else {
                                                                    setFieldErrors((prev) => ({
                                                                        ...prev,
                                                                        discountToDate: undefined,
                                                                    }));
                                                                }
                                                            }
                                                        }}
                                                        onBlur={() => {
                                                            if (!formState.discountFromDate || formState.discountFromDate.trim() === '') {
                                                                setFieldErrors((prev) => ({
                                                                    ...prev,
                                                                    discountFromDate: 'Từ ngày không được bỏ trống',
                                                                }));
                                                            } else {
                                                                const fromDateStr = formState.discountFromDate;
                                                                
                                                                // Kiểm tra không được lớn hơn Đến ngày
                                                                if (formState.discountToDate && formState.discountToDate.trim() !== '') {
                                                                    if (fromDateStr > formState.discountToDate) {
                                                                        setFieldErrors((prev) => ({
                                                                            ...prev,
                                                                            discountFromDate: 'Từ ngày phải nhỏ hơn hoặc bằng Đến ngày',
                                                                        }));
                                                                    } else {
                                                                        setFieldErrors((prev) => ({
                                                                            ...prev,
                                                                            discountFromDate: undefined,
                                                                        }));
                                                                    }
                                                                } else {
                                                                    setFieldErrors((prev) => ({
                                                                        ...prev,
                                                                        discountFromDate: undefined,
                                                                    }));
                                                                }
                                                            }
                                                        }}
                                                        dateFormat="dd/MM/yyyy"
                                                        minDate={new Date()}
                                                        maxDate={formState.discountToDate ? stringToDate(formState.discountToDate) : null}
                                                        placeholderText="dd/MM/yyyy"
                                                        className="date-picker-input"
                                                        autoComplete="off"
                                                        ref={(ref) => {
                                                            fromDateInputRef.current = ref;
                                                        }}
                                                    />
                                                    <i
                                                        className="mdi mdi-calendar date-picker-icon"
                                                        onClick={() => {
                                                            if (fromDateInputRef.current?.input) {
                                                                fromDateInputRef.current.input.focus();
                                                                fromDateInputRef.current.setOpen(true);
                                                            }
                                                        }}
                                                    />
                                                </div>
                                                {fieldErrors.discountFromDate && (
                                                    <div className="field-error">{fieldErrors.discountFromDate}</div>
                                                )}
                                            </div>

                                            <div
                                                className={`text-field text-field--line text-field--floating ${
                                                    formState.discountToDate ? 'is-floating' : ''
                                                }`}
                                            >
                                                <label>Đến ngày</label>
                                                <div className="date-picker-wrapper">
                                                    <DatePicker
                                                        selected={stringToDate(formState.discountToDate)}
                                                        onChange={(date) => {
                                                            const dateString = dateToString(date);
                                                            setFormState((prev) => ({
                                                                ...prev,
                                                                discountToDate: dateString,
                                                            }));
                                                            setFieldErrors((prev) => ({
                                                                ...prev,
                                                                discountToDate: undefined,
                                                            }));
                                                            
                                                            // Re-validate discountFromDate when discountToDate changes
                                                            if (formState.discountFromDate && formState.discountFromDate.trim() !== '') {
                                                                if (formState.discountFromDate > dateString) {
                                                                    setFieldErrors((prev) => ({
                                                                        ...prev,
                                                                        discountFromDate: 'Từ ngày phải nhỏ hơn hoặc bằng Đến ngày',
                                                                    }));
                                                                } else {
                                                                    setFieldErrors((prev) => ({
                                                                        ...prev,
                                                                        discountFromDate: undefined,
                                                                    }));
                                                                }
                                                            }
                                                        }}
                                                        onBlur={() => {
                                                            if (!formState.discountToDate || formState.discountToDate.trim() === '') {
                                                                setFieldErrors((prev) => ({
                                                                    ...prev,
                                                                    discountToDate: 'Đến ngày không được bỏ trống',
                                                                }));
                                                            } else {
                                                                const toDateStr = formState.discountToDate;

                                                                // Kiểm tra phải lớn hơn hoặc bằng Từ ngày
                                                                if (formState.discountFromDate && formState.discountFromDate.trim() !== '') {
                                                                    if (toDateStr < formState.discountFromDate) {
                                                                        setFieldErrors((prev) => ({
                                                                            ...prev,
                                                                            discountToDate: 'Đến ngày phải lớn hơn hoặc bằng Từ ngày',
                                                                        }));
                                                                    } else {
                                                                        setFieldErrors((prev) => ({
                                                                            ...prev,
                                                                            discountToDate: undefined,
                                                                        }));
                                                                    }
                                                                } else {
                                                                    setFieldErrors((prev) => ({
                                                                        ...prev,
                                                                        discountToDate: undefined,
                                                                    }));
                                                                }
                                                            }
                                                        }}
                                                        dateFormat="dd/MM/yyyy"
                                                        minDate={(() => {
                                                            const today = new Date();
                                                            today.setHours(0, 0, 0, 0);
                                                            const fromDate = formState.discountFromDate ? stringToDate(formState.discountFromDate) : null;
                                                            // Min phải là lớn hơn giữa ngày hiện tại và Từ ngày
                                                            if (fromDate && fromDate > today) {
                                                                return fromDate;
                                                            }
                                                            return today;
                                                        })()}
                                                        placeholderText="dd/MM/yyyy"
                                                        className="date-picker-input"
                                                        autoComplete="off"
                                                        ref={(ref) => {
                                                            toDateInputRef.current = ref;
                                                        }}
                                                    />
                                                    <i
                                                        className="mdi mdi-calendar date-picker-icon"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            if (toDateInputRef.current?.input) {
                                                                toDateInputRef.current.input.focus();
                                                                toDateInputRef.current.input.click();
                                                            }
                                                        }}
                                                    />
                                                </div>
                                                {fieldErrors.discountToDate && (
                                                    <div className="field-error">{fieldErrors.discountToDate}</div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </>
                    )}

                </form>
            </div>
            <div className="form-actions">
                <button type="button" className="action-btn action-btn--ghost" onClick={handleNavigateBack}>
                    Đóng
                </button>
                {CREATE && (
                    <button
                        type="button"
                        className="action-btn action-btn--secondary"
                        disabled={isSubmitting}
                        onClick={() => handleSubmitPolicy(2)}
                    >
                        Lưu tạm
                    </button>
                )}
                {CREATE && (
                    <button
                        type="submit"
                        className="action-btn action-btn--primary"
                        form={FORM_ID}
                        disabled={isSubmitting}
                    >
                        Lưu & kích hoạt
                    </button>
                )}
            </div>
        </div>
    );
};

export default PointRedemptionPolicyCreate;


